/* Layer switcher: toggle multiple POI types on the map. */
import { Pill, Hospital, Home, Stethoscope, GraduationCap, Building, ShoppingCart, Activity, Bus, FlaskConical, Briefcase, Building2 } from 'lucide-react';

export const LAYER_GROUPS = [
  {
    id: 'health',
    label: "Sant\u00e9",
    items: [
      { key: 'pharmacie', label: 'Pharmacies', icon: Pill, color: '#DC2626' },
      { key: 'hopital', label: "H\u00f4pitaux", icon: Hospital, color: '#7F1D1D' },
      { key: 'clinique', label: 'Cliniques', icon: Hospital, color: '#BE185D' },
      { key: 'ehpad', label: 'EHPAD', icon: Home, color: '#7C3AED' },
      { key: 'msp', label: 'MSP', icon: Building, color: '#0EA5E9' },
      { key: 'laboratoire', label: 'Laboratoires', icon: FlaskConical, color: '#06B6D4' },
    ],
  },
  {
    id: 'doctors',
    label: 'Prescripteurs',
    items: [
      { key: 'medecin_gen', label: "M\u00e9d. g\u00e9n.", icon: Stethoscope, color: '#10B981' },
      { key: 'medecin_spe', label: "M\u00e9d. sp\u00e9.", icon: Stethoscope, color: '#10B981' },
      { key: 'dentiste', label: 'Dentistes', icon: Stethoscope, color: '#0891B2' },
      { key: 'infirmier', label: 'Infirmiers', icon: Stethoscope, color: '#F59E0B' },
      { key: 'kine', label: 'Kin\u00e9sith\u00e9rapeutes', icon: Stethoscope, color: '#F97316' },
    ],
  },
  {
    id: 'education',
    label: "\u00c9ducation",
    items: [
      { key: 'creche', label: "Cr\u00e8ches", icon: GraduationCap, color: '#60A5FA' },
      { key: 'ecole', label: "\u00c9coles", icon: GraduationCap, color: '#3B82F6' },
      { key: 'college', label: "Coll\u00e8ges", icon: GraduationCap, color: '#2563EB' },
      { key: 'universite', label: "Universit\u00e9s", icon: GraduationCap, color: '#1D4ED8' },
    ],
  },
  {
    id: 'commerce',
    label: 'Commerce & services',
    items: [
      { key: 'supermarche', label: "Supermarch\u00e9s", icon: ShoppingCart, color: '#84CC16' },
      { key: 'centre_commercial', label: 'Centres commerciaux', icon: ShoppingCart, color: '#A3E635' },
      { key: 'banque', label: 'Banques', icon: Building, color: '#16A34A' },
      { key: 'poste', label: 'La Poste', icon: Briefcase, color: '#FACC15' },
      { key: 'salle_sport', label: 'Salles de sport', icon: Activity, color: '#FB923C' },
      { key: 'centre_sportif', label: 'Centres sportifs', icon: Activity, color: '#FDBA74' },
    ],
  },
  {
    id: 'business',
    label: 'Entreprises',
    items: [
      { key: 'entreprise', label: 'Entreprises', icon: Building2, color: '#64748B' },
      { key: 'coworking', label: 'Coworking', icon: Briefcase, color: '#94A3B8' },
      { key: 'administration', label: 'Administration', icon: Briefcase, color: '#475569' },
    ],
  },
  {
    id: 'transport',
    label: 'Transport',
    items: [
      { key: 'gare', label: 'Gares', icon: Bus, color: '#7C3AED' },
      { key: 'bus_stop', label: 'Arr\u00eats de bus', icon: Bus, color: '#A78BFA' },
      { key: 'velo', label: 'Stationnement v\u00e9lo', icon: Bus, color: '#8B5CF6' },
    ],
  },
];

export const ALL_LAYER_KEYS = LAYER_GROUPS.flatMap(g => g.items.map(i => i.key));

export const DEFAULT_LAYERS = new Set([
  'pharmacie', 'hopital', 'ehpad', 'msp', 'medecin_gen', 'medecin_spe', 'laboratoire',
]);

export default function LayerSwitcher({ activeLayers, onChange, counts = {} }) {
  const toggle = (key) => {
    const next = new Set(activeLayers);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange(next);
  };
  const toggleGroup = (group, on) => {
    const next = new Set(activeLayers);
    group.items.forEach(item => {
      if (on) next.add(item.key);
      else next.delete(item.key);
    });
    onChange(next);
  };
  const selectAll = () => onChange(new Set(ALL_LAYER_KEYS));
  const selectNone = () => onChange(new Set());
  const selectDefault = () => onChange(new Set(DEFAULT_LAYERS));

  return (
    <div className="space-y-3" data-testid="layer-switcher">
      <div className="flex gap-1.5">
        <button onClick={selectDefault} data-testid="layer-defaults"
          className="text-[10px] px-2 py-1 rounded-lg bg-[#1B2A4A] text-white font-bold hover:bg-[#0F1E37]">
          Sant&eacute; uniquement
        </button>
        <button onClick={selectAll} data-testid="layer-all"
          className="text-[10px] px-2 py-1 rounded-lg bg-gray-100 text-gray-700 font-bold hover:bg-gray-200">
          Tout
        </button>
        <button onClick={selectNone} data-testid="layer-none"
          className="text-[10px] px-2 py-1 rounded-lg bg-gray-100 text-gray-700 font-bold hover:bg-gray-200">
          Aucun
        </button>
      </div>

      {LAYER_GROUPS.map(group => {
        const groupActive = group.items.every(i => activeLayers.has(i.key));
        const groupSome = group.items.some(i => activeLayers.has(i.key));
        return (
          <div key={group.id} className="bg-white rounded-xl border border-gray-100 p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase text-gray-500">{group.label}</p>
              <button
                onClick={() => toggleGroup(group, !groupActive)}
                data-testid={`group-toggle-${group.id}`}
                className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${
                  groupActive ? 'bg-[#1B2A4A] text-white' : groupSome ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                }`}
              >
                {groupActive ? 'On' : groupSome ? 'Mixte' : 'Off'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1">
              {group.items.map(item => {
                const active = activeLayers.has(item.key);
                const count = counts[item.key] || 0;
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    onClick={() => toggle(item.key)}
                    data-testid={`layer-toggle-${item.key}`}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-semibold transition border-2 ${
                      active ? 'text-white border-transparent' : 'text-gray-600 border-gray-100 hover:border-gray-200 bg-white'
                    }`}
                    style={active ? { backgroundColor: item.color } : {}}
                  >
                    <Icon size={11} className={active ? 'text-white' : ''} style={!active ? { color: item.color } : {}} />
                    <span className="flex-1 text-left truncate">{item.label}</span>
                    <span className={`text-[9px] px-1 rounded ${active ? 'bg-white/20' : 'bg-gray-100 text-gray-500'}`}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
